import {
  Column,
  EnquiryHandle,
  IServerSideGetRowsParams,
  ServerSideCriteria,
  useServerEnquiry,
} from '@access-erp/common';
import { Guid } from 'guid-typescript';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ContractLineGridOutput, Forms, ContractLineGridSort } from '@/graphql/contracts/_generated_/contracts';
import { UserRole } from '@/config/roles.config';
import { CONTRACTS_PER_PAGE } from '@/config/common.config';
import { replaceAdvancedValueType } from '@/components/common/EnquiriesScreen/utils/advancedFilterHelpers';
import { useGetInitialFiltersSet } from '@/components/common/EnquiriesScreen/hooks/useGetInitialFiltersSet';
import { notificationService } from '@/services/notification/notification.client';
import { ContractsEnquiriesScreen } from '@/components/common/EnquiriesScreen/providers/ContractsEnquiriesScreen';
import { getSavedSearch } from '@/components/common/EnquiriesScreen/utils/savedSearch';
import { useGetUserRole } from '@/hooks/identity/useGetUserRole';

import { useColumns } from './hooks/useColumns';
import { useGetGridContractLines } from './hooks/useGetGridContractLines';
import { useRightPanel } from './hooks/useRightPanel';
import PauseContractLineModal from
  '@/pages/ContractDetails/RightPanel/Billing/PauseBilling/components/PauseContractLine/PauseContractLineModal';
import ResumeContractLineModal from
  '@/pages/ContractDetails/RightPanel/Billing/PauseBilling/components/ResumeContractLine/ResumeContractLineModal';
import usePauseContractLine from '@/pages/ContractDetails/RightPanel/Billing/PauseBilling/hooks/usePauseContractLine';
import useResumeContractLine from '@/pages/ContractDetails/RightPanel/Billing/PauseBilling/hooks/useResumeContractLine';
import { useGetReasonCodes } from '@/pages/SystemAdmin/ReasonCodes/hooks/useGetReasonCodes';
import { getReasonTypes } from '@/utils/data-helpers/reasonTypes';
import { PAUSE_BILLING_TYPE } from '@/config/reasonTypes.config';
import { GetPaginatedFilteredGridContractLines } from
  '@/graphql/contracts/_generated_/getPaginatedFilteredGridContractLines.query';
import { useBulkAssignPurchaseOrder } from '../ContractDetails/ContractLines/hooks/useBulkAssignPurchaseOrder';
import { Fields } from
  '../ContractDetails/RightPanel/Billing/AssignPurchaseOrderForLines/components/AssignPurchaseOrderModal/validation';
import AssignPurchaseOrderModal from
  // eslint-disable-next-line max-len
  '../ContractDetails/RightPanel/Billing/AssignPurchaseOrderForLines/components/AssignPurchaseOrderModal/AssignPurchaseOrderModal';
import UnassignPurchaseOrderModal
  from '../ContractDetails/RightPanel/Billing/UnassignPurchaseOrderForLines/UnassignPurchaseOrderModal';
import { useBulkUnassignPurchaseOrder } from '../ContractDetails/ContractLines/hooks/useBulkUnassignPurchaseOrder';
import { groupSelectedLinesByContract } from '@/utils/contractLineHelpers';

type ContractLineWithId = NonNullable<
NonNullable<GetPaginatedFilteredGridContractLines['paginatedFilteredGridContractLines']>['items']
>[number] & {
  id: string;
  contractId: string;
};

const ContractLinesComponent = () => {
  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole>();
  const [selectedContractLinesForPause, setSelectedContractLinesForPause] = useState<any[]>([]);
  const [selectedContractLinesForResume, setSelectedContractLinesForResume] = useState<any[]>([]);
  const gridRef = useRef<EnquiryHandle<ContractLineWithId>>(null);
  const { headerColumns, goToContractLineBySearch } = useColumns();
  const { getSavedFilterIfAny, savedFilters } = useGetInitialFiltersSet();
  const { getUserRole } = useGetUserRole();
  const { getGridContractLines } = useGetGridContractLines();
  const { bulkAssignPurchaseOrder } = useBulkAssignPurchaseOrder(false);
  const { bulkUnassignPurchaseOrder } = useBulkUnassignPurchaseOrder(false);
  const enquiry = useServerEnquiry<ContractLineWithId, any>({
    gridRef: gridRef,
    headerColumns: headerColumns as Column<ContractLineWithId>[],
    initialFilter: replaceAdvancedValueType(savedFilters),
  });

  const contractId = enquiry.selectedHeaderRows[0]?.data?.contractId || '';
  const { pauseContractLine } = usePauseContractLine();
  const { resumeContractLine } = useResumeContractLine();
  const {
    getReasonCodes,
    isLoading: isLoadingReasonCodes,
    reasonCodes
  } = useGetReasonCodes();
  const reasonTypes = useMemo(() => getReasonTypes(), []);

  const searchTextFromQuery = getSavedSearch?.()?.savedSearch;

  useEffect(() => {
    const getInitialData = async () => {
      const roleName = await getUserRole();
      setUserRole(roleName);
    };

    getInitialData();
  }, [getUserRole]);

  const canOpenAssignModal = async () => {
    const contractLines = enquiry.selectedHeaderRows.map(
      (row) => row.data,
    ) as ContractLineGridOutput[];
    const firstAccount = contractLines[0]?.invoiceToCustomerAccountNumber;
    const firstCurrency = contractLines[0]?.contractCurrency;

    const allSameAccount = contractLines.every(
      (cl) => cl?.invoiceToCustomerAccountNumber === firstAccount,
    );
    const allSameCurrency = contractLines.every((cl) => cl?.contractCurrency === firstCurrency);

    if (!allSameAccount) {
      notificationService.open({
        title: 'Error',
        text: 'All selected lines must have the same Invoice To Account',
        type: 'danger',
      });
      return false;
    }
    if (!allSameCurrency) {
      notificationService.open({
        title: 'Error',
        text: 'All selected lines must have the same currency',
        type: 'danger',
      });
      return false;
    }

    return true;
  };

  const transformSelectedRowsToContractLines = (rows: any[]) => {
    return rows.map(row => ({
      id: row.data.id,
      payload: {
        contractLine: { ...row.data, status: row.data.contractLineStatus }
      }
    }));
  };

  const loadSelectedLinesForPause = async () => {
    const selectedLines = transformSelectedRowsToContractLines(enquiry.selectedHeaderRows);
    setSelectedContractLinesForPause(selectedLines);
    getReasonCodes(PAUSE_BILLING_TYPE);
    return true;
  };

  const loadSelectedLinesForResume = async () => {
    const selectedLines = transformSelectedRowsToContractLines(enquiry.selectedHeaderRows);
    setSelectedContractLinesForResume(selectedLines);
    return true;
  };

  const getContractIdFromSelectedRows = () => {
    return contractId;
  };

  const {
    tabsConfiguration,
    isVisibleAssignModal,
    setIsVisibleAssignModal,
    isVisibleUnassignModal,
    setIsVisibleUnassignModal,
    isVisiblePauseModal,
    setIsVisiblePauseModal,
    isVisibleResumeModal,
    setIsVisibleResumeModal,
  } = useRightPanel({
    gridRef: gridRef.current ?? undefined,
    selectedHeaderRows: enquiry.selectedHeaderRows,
    contractId: getContractIdFromSelectedRows(),
    onOpenAssignModal: canOpenAssignModal,
    onOpenPauseModal: loadSelectedLinesForPause,
    onOpenResumeModal: loadSelectedLinesForResume,
  });

  const refreshGrid = () => {
    gridRef.current?.deselectRows();
    gridRef.current?.refreshServerSideGrid({
      purge: true,
    });
  };

  const handleAssignPurchaseOrder = async (values: Fields) => {
    const selectedRowIds = enquiry.selectedHeaderRows.map((row) => row.data?.id).filter(Boolean);

    await bulkAssignPurchaseOrder(values.purchaseOrderId, selectedRowIds as string[], () => {
      setIsVisibleAssignModal(false);
      refreshGrid();
    });
  };

  const handleUnassignPurchaseOrder = async (selectedRowIds: string[]) => {
    await bulkUnassignPurchaseOrder(selectedRowIds, () => {
      setIsVisibleUnassignModal(false);
      refreshGrid();
    });
  };

  const getData = async (
    params: IServerSideGetRowsParams,
    serverSideCriteria: ServerSideCriteria,
  ) => {
    await getSavedFilterIfAny(Forms.Contracts, params);

    const contractLinesData = await getGridContractLines({
      after: serverSideCriteria.nextPageCursor,
      first: CONTRACTS_PER_PAGE,
      filter: {
        summary: enquiry.serverSide?.current?.search ?? searchTextFromQuery,
        ...(enquiry.serverSide?.current?.customFilters ?? {}),
        ...serverSideCriteria.groupingFilters,
      },
      sort: serverSideCriteria.userSetSorting,
      group: serverSideCriteria.groupByColumn as ContractLineGridSort ?? undefined,
    });

    if (
      (!contractLinesData?.items || contractLinesData.items.length === 0) &&
      window.location.pathname.includes('purchase-orders')
    ) {
      notificationService.open({
        title: 'Contract lines not found',
        text: 'No Active or DNR Active contract lines match this PO\'s criteria.',
        type: 'warning',
      });
    }

    setPageIsLoading(false);

    const searchIsCleared = enquiry.serverSide?.current?.search === '';
    if (enquiry.serverSide?.current?.search || searchIsCleared) {
      goToContractLineBySearch?.(enquiry.serverSide?.current?.search);
    }

    return {
      items:
        contractLinesData?.items?.map((el) => ({
          ...el,
          id: el?.id && !Guid.parse(el.id).isEmpty() ? el.id : Guid.create().toString(),
        })) ?? [],
      pageInfo: {
        hasNextPage: contractLinesData?.pageInfo?.hasNextPage || false,
        hasPreviousPage: contractLinesData?.pageInfo?.hasPreviousPage || false,
        endCursor: contractLinesData?.pageInfo?.endCursor,
        startCursor: contractLinesData?.pageInfo?.startCursor,
      },
    };
  };

  const handlePauseContractLine = async (params: any) => {
    const contracts = groupSelectedLinesByContract(selectedContractLinesForPause);
    const success = await pauseContractLine({
      ...params,
      contracts
    });
    setIsVisiblePauseModal(false);

    if (success) {
      refreshGrid();
    }
  };

  const handleResumeContractLine = async (params: any) => {
    const contracts = groupSelectedLinesByContract(selectedContractLinesForResume);
    const success = await resumeContractLine({
      ...params,
      contracts
    });
    setIsVisibleResumeModal(false);

    if (success) {
      refreshGrid();
    }
  };

  return (
    <>
      <ContractsEnquiriesScreen
        ref={gridRef}
        enquiry={enquiry}
        filterForm={Forms.ContractLines}
        formName={'ContractLines'}
        headerProps={{
          primaryTitle: 'Contract Lines',
          secondaryTitle: 'List',
          searchPlaceholder: 'Start typing Contract Number, Account Name, ShipTo or InvoiceTo',
          showExportAll: userRole === UserRole.FULL_ACCESS || userRole === UserRole.ADMIN,
          showExportCurrent: false,
          search: searchTextFromQuery,
        }}
        detailProps={{
          exportFileName: 'Contract Lines',
          rowGroupPanelShow: 'always',
          masterDetail: true,
          getData: getData as any,
          pageSize: CONTRACTS_PER_PAGE,
          suppressRowClickSelection: false,
          loading: pageIsLoading,
        }}
        rightPanelTabs={tabsConfiguration}
      />

      {isVisibleAssignModal && (() => {
        const contractLines = enquiry.selectedHeaderRows.map(
          (row) => row.data,
        ) as ContractLineGridOutput[];
        const firstContractId = contractLines[0]?.contractId;
        const firstInvoiceToCustomerId = contractLines[0]?.invoiceToCustomerId;
        const totalLinesCount = contractLines.length;
        const totalLinesWithPoCount = contractLines.filter(cl => cl?.poNumber).length;
        const contractCurrency =
          contractLines[0]?.contractCurrency === null
            ? undefined
            : contractLines[0]?.contractCurrency;

        return (
          <AssignPurchaseOrderModal
            onClose={() => setIsVisibleAssignModal(false)}
            onSubmit={handleAssignPurchaseOrder}
            invoiceToCustomerId={firstInvoiceToCustomerId}
            totalLinesCount={totalLinesCount}
            totalLinesWithPoCount={totalLinesWithPoCount}
            contractId={firstContractId}
            contractCurrency={contractCurrency}
          />
        );
      })()}

      {isVisibleUnassignModal && (() => {
        const contractLines = enquiry.selectedHeaderRows.map(
          (row) => row.data,
        ) as ContractLineGridOutput[];
        const totalLinesCount = contractLines.length;
        const selectedRowIds = contractLines.map(cl => cl?.id).filter(Boolean);

        return (
          <UnassignPurchaseOrderModal
            isOpen={isVisibleUnassignModal}
            totalLinesCount={totalLinesCount}
            onConfirm={() => handleUnassignPurchaseOrder(selectedRowIds)}
            onClose={() => setIsVisibleUnassignModal(false)}
          />
        );
      })()}

      {isVisiblePauseModal && (
        <PauseContractLineModal
          onClose={() => setIsVisiblePauseModal(false)}
          pauseContractLine={handlePauseContractLine}
          reasonCodes={reasonCodes}
          reasonTypes={reasonTypes}
          selectedLines={selectedContractLinesForPause}
          isLoadingReasonCodes={isLoadingReasonCodes}
        />
      )}

      {isVisibleResumeModal && (
        <ResumeContractLineModal
          onClose={() => setIsVisibleResumeModal(false)}
          resumeContractLine={handleResumeContractLine}
          selectedLines={selectedContractLinesForResume}
          isLoadingReasonCodes={isLoadingReasonCodes}
        />
      )}
    </>
  );
};

export default ContractLinesComponent;
